import { useEffect, useId, useRef, useState } from 'react';
import { createApiClient } from '../http/createApiClient.js';
import { visibleModules } from '../modules/registry.js';
import ToolIcon from './ToolIcon.jsx';
import { useOrbitAIAvailable } from '../orbit-ai/availability.js';
import './AppLauncher.css';

const api = createApiClient({ auth: true });

export default function AppLauncher() {
  const [open, setOpen] = useState(false);
  const orbitAIAvailable = useOrbitAIAvailable();
  const [query, setQuery] = useState('');
  const [modules, setModules] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const root = useRef(null);
  const trigger = useRef(null);
  const search = useRef(null);
  const id = useId();

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    // Refresh on each opening: permission changes must not leave stale links behind.
    setModules(null);
    setError(false);
    api.get('/portal/modules', { signal: controller.signal })
      .then(data => {
        if (!controller.signal.aborted) {
          setModules(visibleModules(data.grants, data.isGlobalAdmin, data.actingAs)
            .filter(module => module.live && module.path));
        }
      })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [open, attempt]);

  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    const onPointerDown = event => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const term = query.trim().toLowerCase();
  const availableModules = (modules || []).filter(module => module.id !== 'orbit-ai' || orbitAIAvailable);
  const shown = availableModules.filter(module =>
    `${module.label} ${module.desc || ''}`.toLowerCase().includes(term));

  return (
    <div className="o-app-launcher" data-app-launcher ref={root}
      onBlur={event => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}>
      <button ref={trigger} type="button" className="o-btn o-btn-ghost o-app-launcher-trigger"
        aria-label="Open apps" title="Open apps" aria-haspopup="dialog"
        aria-expanded={open} aria-controls={open ? id : undefined}
        onClick={() => {
          setModules(null);
          setError(false);
          setQuery('');
          setOpen(value => !value);
        }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          {[4, 10, 16].flatMap(y => [4, 10, 16].map(x =>
            <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" />))}
        </svg>
      </button>
      {open && (
        <section id={id} role="dialog" aria-label="Orbit apps" className="o-popover o-app-launcher-panel">
          <div className="o-app-launcher-heading">
            <strong>Orbit apps</strong>
            <button type="button" className="o-btn o-btn-ghost o-btn-sm" onClick={close}
              aria-label="Close apps">Close</button>
          </div>
          <input ref={search} type="search" className="o-control" aria-label="Find Orbit apps"
            placeholder="Find Orbit apps" value={query} onChange={event => setQuery(event.target.value)} />
          <p className="o-note">Apps open in a new tab.</p>
          {error ? (
            <div className="o-state o-error" role="alert">
              Could not load your apps. Try again.
              <button type="button" className="o-btn o-btn-ghost o-btn-sm"
                onClick={() => setAttempt(value => value + 1)}>Retry</button>
            </div>
          ) : modules === null ? (
            <div className="o-state" role="status">Loading apps…</div>
          ) : availableModules.length === 0 ? (
            <div className="o-state">No apps have been shared with you. Ask an administrator for access.</div>
          ) : shown.length === 0 ? (
            <div className="o-state" role="status">No apps match. Try a different search.</div>
          ) : (
            <div className="o-app-launcher-grid">
              {shown.map(module => (
                <a key={module.id} href={module.path} target="_blank" rel="noopener noreferrer"
                  className="o-app-launcher-link" onClick={close}>
                  <span className="o-app-launcher-icon"><ToolIcon type={module.icon} /></span>
                  <span>{module.label}</span>
                </a>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
